import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, apiDownload } from '../services/api';
import {
  Plus,
  Search,
  FileText,
  Download,
  Send,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  Mail,
  Banknote,
} from 'lucide-react';

interface Invoice {
  id: string;
  invoice_number: string;
  client_id: string;
  client_name: string;
  status: string;
  issue_date: string;
  due_date: string;
  currency: string;
  total: number;
  amount_due: number;
}

interface Client {
  id: string;
  name: string;
  contact_email?: string;
  email?: string;
}

interface PaginatedResponse {
  items: Invoice[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

interface ExchangeRateInfo {
  id: string;
  base_currency: string;
  target_currency: string;
  rate: number;
  rate_date: string;
  source: string;
}

interface SettlementResponse {
  payment_id: string;
  settlement_amount: number;
  settlement_currency: string;
  invoice_amount_applied: number;
  invoice_currency: string;
  exchange_rate: number;
  remaining_due: number;
  status: string;
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-500/10 text-gray-400',
  sent: 'bg-blue-500/10 text-blue-400',
  viewed: 'bg-purple-500/10 text-purple-400',
  partial: 'bg-yellow-500/10 text-yellow-400',
  paid: 'bg-green-500/10 text-green-400',
  overdue: 'bg-red-500/10 text-red-400',
  cancelled: 'bg-gray-500/10 text-gray-500',
};

function formatCurrency(amount: number, currency = 'ZAR'): string {
  const symbols: Record<string, string> = { ZAR: 'R', USD: '$', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || 'R';
  return `${symbol}${amount.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-ZA');
}

export default function Invoices() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);

  // Preview modal state
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  // Send modal state
  const [sendInvoice, setSendInvoice] = useState<Invoice | null>(null);
  const [sendEmail, setSendEmail] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Settlement modal state
  const [settleInvoice, setSettleInvoice] = useState<Invoice | null>(null);
  const [settleAmount, setSettleAmount] = useState('');
  const [settleMethod, setSettleMethod] = useState('bank_transfer');
  const [settleReference, setSettleReference] = useState('');
  const [settleNotes, setSettleNotes] = useState('');
  const [settleRate, setSettleRate] = useState<ExchangeRateInfo | null>(null);
  const [isSettling, setIsSettling] = useState(false);
  const [settleError, setSettleError] = useState<string | null>(null);

  const page = parseInt(searchParams.get('page') || '1');
  const status = searchParams.get('status') || '';

  useEffect(() => {
    const fetchInvoices = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('page', page.toString());
        params.set('page_size', '20');
        if (search) params.set('search', search);
        if (status) params.set('status', status);

        const data = await apiGet<PaginatedResponse>(`/api/invoices?${params}`);
        setInvoices(data.items);
        setTotalPages(data.total_pages);
        setTotal(data.total);
      } catch (err) {
        console.error('Failed to fetch invoices:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoices();
  }, [page, status, search]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (search) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleStatusFilter = (newStatus: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (newStatus) {
      params.set('status', newStatus);
    } else {
      params.delete('status');
    }
    setSearchParams(params);
  };

  const handleDownloadPDF = async (invoiceId: string, invoiceNumber: string) => {
    try {
      const blob = await apiDownload(`/api/invoices/${invoiceId}/pdf`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${invoiceNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
    }
    setActionMenuId(null);
  };

  const handlePreviewInvoice = async (invoice: Invoice) => {
    setActionMenuId(null);
    setPreviewInvoice(invoice);
    setIsPreviewLoading(true);
    try {
      const blob = await apiDownload(`/api/invoices/${invoice.id}/pdf`);
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (err) {
      console.error('Failed to load preview:', err);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewInvoice(null);
  };

  const openSendModal = async (invoice: Invoice) => {
    setActionMenuId(null);
    setSendInvoice(invoice);
    setSendError(null);
    setSendMessage('');
    // Fetch client to get email
    try {
      const client = await apiGet<Client>(`/api/clients/${invoice.client_id}`);
      setSendEmail(client.contact_email || client.email || '');
    } catch {
      setSendEmail('');
    }
  };

  const closeSendModal = () => {
    setSendInvoice(null);
    setSendEmail('');
    setSendMessage('');
    setSendError(null);
  };

  const handleSendInvoice = async () => {
    if (!sendInvoice || !sendEmail) return;
    setIsSending(true);
    setSendError(null);
    try {
      await apiPost(`/api/invoices/${sendInvoice.id}/send`, {
        email_to: sendEmail,
        email_body: sendMessage || undefined,
      });
      closeSendModal();
      // Refresh list
      const params = new URLSearchParams(searchParams);
      setSearchParams(params);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send invoice');
    } finally {
      setIsSending(false);
    }
  };

  const openSettleModal = async (invoice: Invoice) => {
    setActionMenuId(null);
    setSettleInvoice(invoice);
    setSettleAmount('');
    setSettleMethod('bank_transfer');
    setSettleReference('');
    setSettleNotes('');
    setSettleError(null);
    // Fetch current exchange rate if invoice is in foreign currency
    if (invoice.currency && invoice.currency !== 'ZAR') {
      try {
        const rate = await apiGet<ExchangeRateInfo>(
          `/api/settings/exchange-rates/current?base_currency=${invoice.currency}&target_currency=ZAR`
        );
        setSettleRate(rate);
        // Pre-fill with ZAR equivalent of amount_due
        const zarEquivalent = (invoice.amount_due * rate.rate).toFixed(2);
        setSettleAmount(zarEquivalent);
      } catch {
        setSettleRate(null);
      }
    } else {
      setSettleRate(null);
      setSettleAmount(invoice.amount_due.toFixed(2));
    }
  };

  const closeSettleModal = () => {
    setSettleInvoice(null);
    setSettleAmount('');
    setSettleRate(null);
    setSettleError(null);
  };

  const handleSettleInvoice = async () => {
    if (!settleInvoice || !settleAmount) return;
    setIsSettling(true);
    setSettleError(null);
    try {
      await apiPost<SettlementResponse>(`/api/invoices/${settleInvoice.id}/settle`, {
        settlement_amount: parseFloat(settleAmount),
        settlement_currency: 'ZAR',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: settleMethod || undefined,
        reference: settleReference || undefined,
        notes: settleNotes || undefined,
      });
      closeSettleModal();
      // Refresh list
      const params = new URLSearchParams(searchParams);
      setSearchParams(params);
    } catch (err) {
      setSettleError(err instanceof Error ? err.message : 'Failed to record payment');
    } finally {
      setIsSettling(false);
    }
  };

  const statusFilters = ['', 'draft', 'sent', 'viewed', 'partial', 'paid', 'overdue', 'cancelled'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Invoices</h1>
          <p className="text-gray-400">{total} total invoices</p>
        </div>
        <Link
          to="/admin/invoices/new"
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Invoice
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search invoices..."
              className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </form>

        <div className="flex gap-2 flex-wrap">
          {statusFilters.map((s) => (
            <button
              key={s}
              onClick={() => handleStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-colors ${
                status === s
                  ? 'bg-primary-500 text-white'
                  : 'bg-dark-800 text-gray-400 hover:text-white'
              }`}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-dark-900 rounded-xl border border-dark-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-800">
                <th className="text-left p-4 text-sm font-medium text-gray-400">Invoice</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Client</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Status</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Issue Date</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Due Date</th>
                <th className="text-right p-4 text-sm font-medium text-gray-400">Amount</th>
                <th className="text-right p-4 text-sm font-medium text-gray-400">Due</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-800">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-gray-500">
                    No invoices found
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-dark-800/50">
                    <td className="p-4">
                      <Link
                        to={`/admin/invoices/${invoice.id}`}
                        className="flex items-center gap-2 text-white hover:text-primary-500"
                      >
                        <FileText className="w-4 h-4" />
                        {invoice.invoice_number}
                      </Link>
                    </td>
                    <td className="p-4 text-gray-300">{invoice.client_name}</td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                          statusColors[invoice.status] || statusColors.draft
                        }`}
                      >
                        {invoice.status}
                      </span>
                    </td>
                    <td className="p-4 text-gray-400">{formatDate(invoice.issue_date)}</td>
                    <td className="p-4 text-gray-400">{formatDate(invoice.due_date)}</td>
                    <td className="p-4 text-right text-white">
                      {formatCurrency(invoice.total, invoice.currency)}
                    </td>
                    <td className="p-4 text-right">
                      <span className={invoice.amount_due > 0 ? 'text-yellow-400' : 'text-green-400'}>
                        {formatCurrency(invoice.amount_due, invoice.currency)}
                      </span>
                    </td>
                    <td className="p-4 text-right relative">
                      <button
                        onClick={() => setActionMenuId(actionMenuId === invoice.id ? null : invoice.id)}
                        className="p-2 hover:bg-dark-700 rounded-lg"
                      >
                        <MoreHorizontal className="w-4 h-4 text-gray-400" />
                      </button>
                      {actionMenuId === invoice.id && (
                        <div className="absolute right-0 bottom-full mb-2 w-48 bg-dark-800 rounded-lg shadow-lg border border-dark-700 z-50">
                          <Link
                            to={`/admin/invoices/${invoice.id}`}
                            className="flex items-center gap-2 px-4 py-2 text-gray-300 hover:bg-dark-700"
                          >
                            <FileText className="w-4 h-4" />
                            View Details
                          </Link>
                          <button
                            onClick={() => handlePreviewInvoice(invoice)}
                            className="flex items-center gap-2 w-full px-4 py-2 text-gray-300 hover:bg-dark-700"
                          >
                            <Eye className="w-4 h-4" />
                            Preview PDF
                          </button>
                          <button
                            onClick={() => handleDownloadPDF(invoice.id, invoice.invoice_number)}
                            className="flex items-center gap-2 w-full px-4 py-2 text-gray-300 hover:bg-dark-700"
                          >
                            <Download className="w-4 h-4" />
                            Download PDF
                          </button>
                          {(invoice.status === 'draft' || invoice.status === 'sent') && (
                            <button
                              onClick={() => openSendModal(invoice)}
                              className="flex items-center gap-2 w-full px-4 py-2 text-gray-300 hover:bg-dark-700"
                            >
                              <Mail className="w-4 h-4" />
                              Send Invoice
                            </button>
                          )}
                          {['sent', 'viewed', 'partial', 'overdue'].includes(invoice.status) && (
                            <button
                              onClick={() => openSettleModal(invoice)}
                              className="flex items-center gap-2 w-full px-4 py-2 text-green-400 hover:bg-dark-700"
                            >
                              <Banknote className="w-4 h-4" />
                              Record Payment
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-dark-800">
            <p className="text-sm text-gray-400">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set('page', (page - 1).toString());
                  setSearchParams(params);
                }}
                disabled={page <= 1}
                className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
              >
                <ChevronLeft className="w-5 h-5 text-gray-400" />
              </button>
              <button
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set('page', (page + 1).toString());
                  setSearchParams(params);
                }}
                disabled={page >= totalPages}
                className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
              >
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewInvoice && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
          <div className="bg-dark-900 rounded-xl border border-dark-700 w-full max-w-4xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-dark-700">
              <h2 className="text-lg font-semibold text-white">
                Preview: {previewInvoice.invoice_number}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownloadPDF(previewInvoice.id, previewInvoice.invoice_number)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-dark-700 hover:bg-dark-600 text-white rounded-lg text-sm"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                <button
                  onClick={() => {
                    closePreview();
                    openSendModal(previewInvoice);
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm"
                >
                  <Send className="w-4 h-4" />
                  Send
                </button>
                <button
                  onClick={closePreview}
                  className="p-1.5 hover:bg-dark-700 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden bg-gray-100">
              {isPreviewLoading ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-600">Loading preview...</p>
                </div>
              ) : previewUrl ? (
                <iframe
                  src={previewUrl}
                  className="w-full h-full"
                  title={`Preview ${previewInvoice.invoice_number}`}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-600">Failed to load preview</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Send Invoice Modal */}
      {sendInvoice && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
          <div className="bg-dark-900 rounded-xl border border-dark-700 w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-dark-700">
              <h2 className="text-lg font-semibold text-white">
                Send Invoice: {sendInvoice.invoice_number}
              </h2>
              <button
                onClick={closeSendModal}
                className="p-1.5 hover:bg-dark-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  To
                </label>
                <input
                  type="email"
                  value={sendEmail}
                  onChange={(e) => setSendEmail(e.target.value)}
                  placeholder="client@example.com"
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Message (optional)
                </label>
                <textarea
                  value={sendMessage}
                  onChange={(e) => setSendMessage(e.target.value)}
                  placeholder="Add a personal message..."
                  rows={4}
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  If left empty, a standard message will be included.
                </p>
              </div>
              {sendError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {sendError}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-dark-700">
              <button
                onClick={closeSendModal}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvoice}
                disabled={!sendEmail || isSending}
                className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                <Mail className="w-4 h-4" />
                {isSending ? 'Sending...' : 'Send Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settlement Modal */}
      {settleInvoice && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
          <div className="bg-dark-900 rounded-xl border border-dark-700 w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-dark-700">
              <h2 className="text-lg font-semibold text-white">
                Record Payment: {settleInvoice.invoice_number}
              </h2>
              <button
                onClick={closeSettleModal}
                className="p-1.5 hover:bg-dark-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Invoice Summary */}
              <div className="p-3 bg-dark-800 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Invoice Total</span>
                  <span className="text-white font-medium">
                    {formatCurrency(settleInvoice.total, settleInvoice.currency)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Amount Due</span>
                  <span className="text-yellow-400 font-medium">
                    {formatCurrency(settleInvoice.amount_due, settleInvoice.currency)}
                  </span>
                </div>
                {settleRate && (
                  <>
                    <div className="border-t border-dark-700 pt-2 flex justify-between text-sm">
                      <span className="text-gray-400">Exchange Rate</span>
                      <span className="text-primary-400 font-medium">
                        1 {settleInvoice.currency} = R{settleRate.rate.toLocaleString('en-ZA', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Amount Due in ZAR</span>
                      <span className="text-white font-medium">
                        R{(settleInvoice.amount_due * settleRate.rate).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Payment Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Amount Received (ZAR)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={settleAmount}
                    onChange={(e) => setSettleAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                {settleRate && settleAmount && parseFloat(settleAmount) > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    = {formatCurrency(parseFloat(settleAmount) / settleRate.rate, settleInvoice.currency)} equivalent
                  </p>
                )}
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Payment Method
                </label>
                <select
                  value={settleMethod}
                  onChange={(e) => setSettleMethod(e.target.value)}
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                  <option value="cheque">Cheque</option>
                  <option value="paypal">PayPal</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Reference */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Reference
                </label>
                <input
                  type="text"
                  value={settleReference}
                  onChange={(e) => setSettleReference(e.target.value)}
                  placeholder="Payment reference or transaction ID"
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={settleNotes}
                  onChange={(e) => setSettleNotes(e.target.value)}
                  placeholder="Additional notes..."
                  rows={2}
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>

              {settleError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {settleError}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-dark-700">
              <button
                onClick={closeSettleModal}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSettleInvoice}
                disabled={!settleAmount || parseFloat(settleAmount) <= 0 || isSettling}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                <Banknote className="w-4 h-4" />
                {isSettling ? 'Processing...' : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
